import React from 'react';
import { AlertTriangle, Lock, Hourglass, CheckCircle2, Info } from 'lucide-react';

/**
 * Bannière affichée en haut du dashboard client selon son statut.
 * Renvoie aussi des informations sur les fonctionnalités disponibles.
 */
const STATUS_CONFIG = {
  actif: {
    show: false,
    canTransfer: true,
    canCard: true,
    canEditProfile: true,
  },
  en_attente: {
    show: true,
    title: 'Compte en cours de validation',
    desc: "Votre compte est en attente de validation par notre équipe. Vous pouvez consulter votre profil mais les opérations bancaires ne sont pas encore disponibles.",
    color: 'amber',
    icon: Hourglass,
    canTransfer: false,
    canCard: false,
    canEditProfile: true,
  },
  inactif: {
    show: true,
    title: 'Compte inactif',
    desc: "Votre compte est actuellement inactif. Pour le réactiver, veuillez contacter notre service client à contact@bfigroupefr.com.",
    color: 'slate',
    icon: Info,
    canTransfer: false,
    canCard: false,
    canEditProfile: true,
  },
  bloque: {
    show: true,
    title: 'Compte bloqué',
    desc: "Pour des raisons de sécurité, votre compte est temporairement bloqué. Toutes les opérations sont suspendues. Contactez notre service client : contact@bfigroupefr.com.",
    color: 'rose',
    icon: Lock,
    canTransfer: false,
    canCard: false,
    canEditProfile: false,
  },
  suspendu: {
    show: true,
    title: 'Compte suspendu',
    desc: "Votre compte est suspendu. Veuillez contacter notre service client pour plus d'informations.",
    color: 'rose',
    icon: AlertTriangle,
    canTransfer: false,
    canCard: false,
    canEditProfile: false,
  },
  ferme: {
    show: true,
    title: 'Compte fermé',
    desc: "Ce compte a été clôturé. Pour rouvrir un compte, veuillez nous contacter.",
    color: 'rose',
    icon: Lock,
    canTransfer: false,
    canCard: false,
    canEditProfile: false,
  },
};

const COLOR_STYLES = {
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  rose: 'bg-rose-50 border-rose-200 text-rose-900',
  slate: 'bg-slate-50 border-slate-200 text-slate-900',
};

export function getStatusConfig(statut) {
  return STATUS_CONFIG[statut] || STATUS_CONFIG.actif;
}

export default function AccountStatusBanner({ statut, message }) {
  const cfg = getStatusConfig(statut);
  if (!cfg.show) return null;
  const Icon = cfg.icon || Info;
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${COLOR_STYLES[cfg.color] || COLOR_STYLES.slate}`} data-testid="account-status-banner">
      <Icon className="w-5 h-5 shrink-0 mt-0.5"/>
      <div className="flex-1">
        <div className="font-semibold text-sm">{cfg.title}</div>
        <div className="text-sm mt-1 opacity-90">{message || cfg.desc}</div>
      </div>
    </div>
  );
}

/**
 * Bouton qui peut être désactivé selon les permissions du statut.
 * Utiliser avec une checkPermission key ('canTransfer', 'canCard', etc).
 */
export function StatusGuard({ statut, permission, children, fallback = null }) {
  const cfg = getStatusConfig(statut);
  if (cfg[permission]) return children;
  return fallback;
}
